<template>
  <div class="bg-white p-4 lg:p-8 mb-4 md:mb-0 flex">
    <!--    reset will be true if the entered email is already in the DB -->
    <div
      v-if="reset"
      class="w-full"
    >
      <div class="bg-green-50 text-green-600 w-full border border-green-500 px-4 pt-4 pb-2 rounded">
        <strong>We've sent you a password reset email!</strong>
        <p class="mt-4 text-sm">Please check your email for further instructions.</p>
        <div class="mt-4 text-xs font-bold">
          <a
            href="#"
            @click.prevent="reset = false"
            class="flex items-center py-2 space-x-2 hover:text-green-500"
          >
            <span>Try a different email</span>
            <ion-icon name="arrow-forward" />
          </a>
        </div>
      </div>
    </div>
    <div
      v-else
      class="w-full"
    >
      <div class="flex flex-col h-full lg:ml-24">
        <div class="my-auto w-full flex flex-col h-full">
          <div class="pt-4 pr-4 pb-4 flex justify-start">
            <span class="flex items-center">
              <img
                class="h-8 w-auto sm:h-7 logo"
                src="/images/black_logo.png"
                alt="Financial Cents Logo"
              />
            </span>
          </div>
          <div class="flex flex-col grow">
            <div class="my-auto">
              <div class="text-left">
                <h1 class="text-left text-3xl font-bold mb-1 text-blue-900">
                  {{ abSignupTitle }}
                </h1>
                <p class="mb-4 md:mb-8 text-base text-gray-500">Get set up in just a few minutes</p>
              </div>
              <form class="bg-white rounded registration">
                <div class="mb-6 flex flex-col sm:flex-row gap-4">
                  <div class="flex-1">
                    <v-label
                      class="block mb-1"
                      title="First name"
                    >
                      <template #content><span class="text-green-600">*</span></template>
                    </v-label>
                    <on-boarding-input
                      input-type="text"
                      name="first_name"
                      v-model="form.first_name"
                      class="w-full rounded-lg"
                      placeholder-text="First name"
                    />
                    <p
                      class="help is-danger text-xs"
                      v-if="form.errors.has('first_name')"
                    >
                      {{ form.errors.first("first_name") }}
                    </p>
                  </div>
                  <div class="flex-1">
                    <v-label
                      class="block mb-1"
                      title="Last name"
                    >
                      <template #content><span class="text-green-600">*</span></template>
                    </v-label>
                    <on-boarding-input
                      input-type="text"
                      name="last_name"
                      v-model="form.last_name"
                      class="w-full rounded-lg"
                      placeholder-text="Last name"
                    />
                    <p
                      class="help is-danger text-xs"
                      v-if="form.errors.has('last_name')"
                    >
                      {{ form.errors.first("last_name") }}
                    </p>
                  </div>
                </div>
                <div class="mb-6">
                  <v-label
                    class="block mb-1"
                    title="Work email address"
                  >
                    <template #content><span class="text-green-600">*</span></template>
                  </v-label>
                  <label
                    class="block text-xs font-medium text-gray-500 relative"
                    for="email"
                  >
                    <on-boarding-input
                      input-type="text"
                      name="email"
                      v-model="form.email"
                      class="w-full rounded-lg"
                      placeholder-text="Work email address"
                    />
                    <span class="absolute placeholder-icon mt-[3px]">
                      <ion-icon
                        name="mail-outline"
                        class="text-sm text-gray-500"
                      />
                    </span>
                  </label>
                  <p
                    class="help is-danger text-xs"
                    v-if="form.errors.has('email')"
                  >
                    {{ form.errors.first("email") }}
                  </p>
                  <p
                    class="help is-danger text-xs"
                    v-if="throttled"
                  >
                    Too many attempts to create an account. Please wait 15 mins and try again.
                  </p>
                </div>
                <div class="mb-6">
                  <v-label
                    class="block mb-1"
                    title="Password"
                  >
                    <template #content><span class="text-green-600">*</span></template>
                  </v-label>
                  <label
                    class="block text-xs font-medium text-gray-500 relative"
                    for="password"
                  >
                    <password-validator
                      ref="PasswordValidator"
                      @password-verified="passwordValidated = $event"
                    >
                      <on-boarding-input
                        :input-type="showPassword ? 'text' : 'password'"
                        name="password"
                        v-model="form.password"
                        class="tooltip-input-focus w-full rounded-lg"
                        :class="{
                          'border-red-400':
                            form.password.length > 0 && (!passwordValidated || form.errors.has('password')),
                        }"
                        placeholder-text="Password"
                        @input="password_check"
                      />
                      <span class="absolute placeholder-icon mt-[3px]">
                        <ion-icon
                          name="lock-closed-outline"
                          class="text-sm text-gray-500"
                        />
                      </span>
                      <span
                        class="absolute placeholder-icon-right cursor-pointer mt-[3px]"
                        @click.prevent="toggleShowPassword"
                      >
                        <ion-icon
                          :name="showPassword ? 'eye-outline' : 'eye-off-outline'"
                          class="text-sm text-gray-500"
                        ></ion-icon>
                      </span>
                      <span
                        v-if="passwordValidated && !form.errors.has('password')"
                        class="absolute password-check-right cursor-pointer rounded-full border-2 icon h-6 w-6 bg-green-600 flex items-center"
                      >
                        <ion-icon
                          name="checkmark-outline"
                          class="rounded-full h-4 w-4 text-white"
                        />
                      </span>
                    </password-validator>
                  </label>
                  <p
                    class="help is-danger text-xs"
                    v-if="form.errors.has('password')"
                  >
                    {{ form.errors.first("password") }}
                  </p>
                </div>

                <div
                  class="border-gray-300"
                  :class="showGoogleOption || showAboutUsOther || showReferral || showYouTubeOption ? 'mb-4' : 'mb-6'"
                >
                  <v-label
                    class="block mb-1"
                    title="How did you hear about us?"
                  >
                    <template #content><span class="text-green-600">*</span></template>
                  </v-label>
                  <div
                    class="relative inline-block align-middle w-full btn-group hover:border-green-600 cursor-pointer z-10"
                  >
                    <select
                      v-model="form.about_us"
                      class="border rounded-lg border-gray-300 text-sm text-gray-500 dropdown-toggle w-full p-3 flex focus:outline-none focus:border-green-500 active:border-green-500"
                    >
                      <option value="">Please select one</option>
                      <option
                        v-for="(about_us_option, index) in about_us_options"
                        :key="'about_us_option' + index"
                      >
                        {{ about_us_option }}
                      </option>
                    </select>
                  </div>
                  <p
                    class="help is-danger text-xs"
                    v-if="form.errors.has('about_us')"
                  >
                    {{ form.errors.first("about_us") }}
                  </p>
                </div>
                <div
                  v-if="showGoogleOption"
                  class="mb-6"
                >
                  <label class="block mb-1 text-xs font-medium text-gray-600"> </label>
                  <label
                    class="block text-xs font-medium text-gray-500 relative"
                    for="google_option"
                  >
                    <on-boarding-input
                      input-type="text"
                      name="google_option"
                      v-model="form.google_option"
                      class="w-full p-3 pl-12-px"
                      :placeholder-text="getPlaceHolderText()"
                    />
                  </label>
                  <p
                    class="help is-danger text-xs"
                    v-if="form.errors.has('google_option')"
                  >
                    {{ form.errors.first("google_option") }}
                  </p>
                </div>
                <div
                  v-if="showYouTubeOption"
                  class="mb-6"
                >
                  <v-label
                    class="block mb-1"
                    title="Which YouTube video or channel brought you to Financial Cents?"
                  >
                    <template #content><span class="is-danger">*</span></template>
                  </v-label>
                  <div
                    class="relative inline-block align-middle w-full btn-group hover:border-green-600 cursor-pointer z-10"
                  >
                    <select
                      v-model="form.youtube_option"
                      class="border rounded-lg border-gray-300 text-sm text-gray-500 dropdown-toggle w-full p-3 flex focus:outline-none focus:border-green-500 active:border-green-500"
                    >
                      <option value="">Please select one</option>
                      <option value="Financial Cents YouTube Channel">Financial Cents YouTube Channel</option>
                      <option value="Jason Staats">Jason Staats</option>
                      <option value="Logan Graf">Logan Graf</option>
                      <option value="Your CFO Guy">Your CFO Guy</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <p
                    class="help is-danger text-xs"
                    v-if="form.errors.has('youtube_option')"
                  >
                    {{ form.errors.first("youtube_option") }}
                  </p>
                </div>
                <div
                  v-if="showYouTubeOther"
                  class="mb-6"
                >
                  <label class="block mb-1 text-xs font-medium text-gray-600"> </label>
                  <label
                    class="block text-xs font-medium text-gray-500 relative"
                    for="youtube_other"
                  >
                    <on-boarding-input
                      input-type="text"
                      name="youtube_other"
                      v-model="form.youtube_other"
                      class="w-full pl-12-px"
                      placeholder-text="Please specify which YouTube video or channel"
                    />
                  </label>
                  <p
                    class="help is-danger text-xs"
                    v-if="form.errors.has('youtube_other')"
                  >
                    {{ form.errors.first("youtube_other") }}
                  </p>
                </div>
                <div
                  v-if="showReferral"
                  class="mb-6"
                >
                  <label class="block mb-1 text-xs font-medium text-gray-600"> </label>
                  <label
                    class="block mb-2 text-xs font-medium text-gray-500 relative"
                    for="referral"
                  >
                    <on-boarding-input
                      input-type="text"
                      name="referral"
                      v-model="form.referral"
                      class="w-full pl-12-px"
                      placeholder-text="Who referred you?"
                    />
                  </label>
                  <p
                    class="help is-danger"
                    v-if="form.errors.has('referral')"
                  >
                    {{ form.errors.first("referral") }}
                  </p>
                </div>
                <div
                  v-if="showAboutUsOther"
                  class="mb-6"
                >
                  <label class="block mb-1 text-xs font-medium text-gray-600"> </label>
                  <label
                    class="block mb-2 text-xs font-medium text-gray-500 relative"
                    for="about_us_other"
                  >
                    <on-boarding-input
                      input-type="text"
                      name="about_us_other"
                      v-model="form.about_us_other"
                      class="w-full pl-12-px"
                      :placeholder-text="getPlaceHolderText()"
                    />
                  </label>
                  <p
                    class="help is-danger"
                    v-if="form.errors.has('about_us_other')"
                  >
                    {{ form.errors.first("about_us_other") }}
                  </p>
                </div>
                <div class="mb-4">
                  <div class="mb-4">
                    <span class="text-xs text-gray-500 items-center justify-center">
                      By clicking "Sign up for free trial" you agree to Financial Cents'
                      <a
                        href="https://financial-cents.com/terms-of-service"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="text-green-600 underline hover:text-green-500"
                      >
                        Terms and Conditions
                      </a>
                      and
                      <a
                        href="https://app.financial-cents.com/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="text-green-600 underline hover:text-green-500"
                      >
                        Privacy Policy.
                      </a>
                    </span>
                  </div>
                  <v-button
                    :loading="loading"
                    :disabled="loading || !passwordValidated"
                    type="submit"
                    @click.prevent="submit"
                    class="rounded-lg w-full h-12 px-4 py-2 text-white"
                    :class="{
                      'bg-green-500 cursor-not-allowed': !passwordValidated,
                      'bg-green-600 hover:bg-green-700': passwordValidated,
                    }"
                  >
                    <template
                      #title
                      v-if="!loading"
                      >Sign up for free trial</template
                    >
                  </v-button>
                </div>

                <div class="text-sm text-gray-500">
                  Already have an account?
                  <a
                    href="/login"
                    class="text-xs text-green-600 underline hover:text-green-500"
                    >Login</a
                  >
                </div>

                <div class="text-gray-600 text-xs">
                  Copyright &copy; 2017-{{ new Date().getFullYear() }}
                  <a href="/">Financial Cents LLC.</a>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VButton from "../../../ClientPortal/components/VButton.vue"
import OnBoardingInput from "./form/OnBoardingInput.vue"
import Form from "../../helpers/Form"
import VLabel from "./form/VLabel.vue"
import PasswordValidator from "./PasswordValidator.vue"

export default {
  name: "Register",

  components: {
    VButton,
    OnBoardingInput,
    VLabel,
    PasswordValidator,
  },

  emits: ["set-registration-detail", "next-step", "registered"],

  data() {
    return {
      loading: false,
      throttled: false,
      showPassword: false,
      showGoogleOption: false,
      showAboutUsOther: false,
      showReferral: false,
      showYouTubeOption: false,
      showYouTubeOther: false,
      passwordValidated: false,
      form: new Form({
        first_name: "",
        last_name: "",
        email: "",
        password: "",
        google_option: "",
        about_us: "",
        about_us_other: "",
        referral: "",
        youtube_option: "",
        youtube_other: "",
        recaptcha_action: "register",
        recaptcha: "",
        storage_region: "oregon",
      }),
      about_us_options: [
        "Facebook",
        "Google",
        "LinkedIn",
        "YouTube",
        "Referral",
        "Conference",
        // 'Online events', TODO: tosin requested that we remove this but keeping in case we need it back
        "Accounting communities",
        "Other",
      ],
      reset: false,
    }
  },

  watch: {
    "form.about_us": {
      handler(value) {
        this.form.google_option = ""
        this.form.about_us_other = ""
        this.form.referral = ""
        this.form.youtube_option = ""
        this.form.youtube_other = ""
        this.showGoogleOption = false
        this.showAboutUsOther = false
        this.showReferral = false
        this.showYouTubeOption = false
        this.showYouTubeOther = false

        switch (value) {
          case "Google":
          case "Conference":
          case "Online events":
          case "Accounting communities":
            this.showGoogleOption = true
            return
          case "YouTube":
            this.showYouTubeOption = true
            return
          case "Referral":
            this.showReferral = true
            return
          case "Other":
            this.showAboutUsOther = true
            return
        }
      },
    },
    "form.youtube_option": {
      handler(value) {
        this.form.youtube_other = ""
        this.showYouTubeOther = value === "Other"
      },
    },
  },

  computed: {
    abSignupTitle() {
      return this.$route.query?.ab_signup === "try_free" ? "Try it for free" : "Start your free 14-day trial"
    },
  },

  methods: {
    password_check() {
      this.$refs.PasswordValidator.password_check(this.form.password)
    },

    initRecaptcha() {
      const _that = this
      return new Promise((resolve) => {
        if (window.FinancialCents.env === "production") {
          grecaptcha.ready(function () {
            grecaptcha.execute(window.keys.recaptcha, { action: "register" }).then(function (token) {
              _that.form.recaptcha = token
              resolve()
            })
          })
        } else {
          resolve()
        }
      })
    },

    async submit() {
      this.loading = true
      this.throttled = false
      try {
        await this.initRecaptcha()
        const { data } = await this.form.post(`/register`, {
          ab_signup: this.$route.query?.ab_signup,
        })
        this.form.errors.clearAll()

        this.$emit("set-registration-detail", this.form.data({}))
        this.$emit("registered", { redirect_url: data.redirect_url })
        this.$emit("next-step", true)
      } catch (e) {
        if (e.response && e.response.status === 409) {
          this.reset = true
        } else if (e.response && e.response.status === 429) {
          this.throttled = true
        } else if (e.response && e.response.status === 403) {
          alert("You are not authorized to perform this action.")
        } else if (!e.response || e.response.status !== 422) {
          alert("Unable to process your request. Please try refreshing the page or contact us.")
        }
        console.error(e)
      }
      this.loading = false
    },

    toggleShowPassword() {
      this.showPassword = !this.showPassword
    },

    getPlaceHolderText() {
      switch (this.form.about_us) {
        case "Google":
          return "What phrase did you type into google?"

        case "Referral":
          return "Who referred you?"

        case "Conference":
          return `What's the name of the conference?`

        case "Online events":
          return `What's the name of the online event?`

        case "Accounting communities":
          return `What's the name of the community?`

        default:
          return `Where did you hear about us?`
      }
    },
  },
}
</script>

<style scoped>
h1 {
  font-family: "Sora", sans-serif;
}
</style>
